import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

interface NavigationProps {
  activeSection?: string;
}

const Navigation: React.FC<NavigationProps> = () => {
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState({ flag: '🇺🇸', name: 'English' });

  const languages = [
    { flag: '🇺🇸', name: 'English' },
    { flag: '🇪🇸', name: 'Español' },
    { flag: '🇫🇷', name: 'Français' },
    { flag: '🇩🇪', name: 'Deutsch' },
    { flag: '🇯🇵', name: '日本語' },
  ];

  const navItems = [
    { label: 'Applications', hasDropdown: true },
    { label: 'Templates', hasDropdown: false },
    { label: 'Resources', hasDropdown: true },
    { label: 'Company', hasDropdown: true },
    { label: 'Pricing', hasDropdown: false },
    { label: 'Blog', hasDropdown: false }
  ];

  const handleNavigation = (section: string) => {
    // In a real app, this would use react-router-dom
    console.log(`Navigating to ${section}`);
  };

  const handleLanguageSelect = (language: { flag: string; name: string }) => {
    setSelectedLanguage(language);
    setIsLanguageDropdownOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 bg-transparent backdrop-blur-lg border-b border-gray-800/30"
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => handleNavigation('home')}
          >
            <span className="text-xl font-semibold text-white">
              Energent.ai
            </span>
          </motion.div>

          {/* Right side - Navigation Links and Language Selector */}
          <div className="flex items-center space-x-8">
            {/* Navigation Links */}
            <div className="hidden lg:flex items-center space-x-6">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative group"
                >
                  <button
                    onClick={() => handleNavigation(item.label.toLowerCase())}
                    className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center space-x-1 py-2 px-3 rounded-lg hover:bg-gray-800/50"
                  >
                    <span className="text-sm font-medium">{item.label}</span>
                    {item.hasDropdown && (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </motion.div>
              ))}
            </div>

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="hidden md:flex items-center space-x-2 text-gray-300 hover:text-white transition-colors duration-200 py-2 px-3 rounded-lg hover:bg-gray-800/50"
              >
                <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400"></div>
                <span className="text-sm">{selectedLanguage.flag}</span>
                <span className="text-sm">{selectedLanguage.name}</span>
                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Language Dropdown */}
              <AnimatePresence>
                {isLanguageDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50"
                  >
                    {languages.map((language, index) => (
                      <button
                        key={index}
                        onClick={() => handleLanguageSelect(language)}
                        className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-800 transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg ${
                          selectedLanguage.name === language.name ? 'bg-gray-800 text-white' : 'text-gray-300'
                        }`}
                      >
                        <span className="text-lg">{language.flag}</span>
                        <span className="text-sm">{language.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Overlay to close dropdown when clicking outside */}
              {isLanguageDropdownOpen && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsLanguageDropdownOpen(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navigation;